// features/common/PhotoMetadataOverlay.js
// Reusable semi-transparent ribbon overlay for displaying photo geo-metadata
// Used in both CameraModal (live) and PhotoViewModal (saved photos)

import React from "react";
import {
  formatBearing,
  formatCoordinates,
  formatElevation,
  formatAccuracy,
  toMGRS,
  formatCapturedAt,
  formatCapturedAtUTC,
} from "../../utils/coordinateUtils";
import styles from "./PhotoMetadataOverlay.module.css";

/**
 * PhotoMetadataOverlay - Renders geo-metadata ribbons over a photo
 *
 * @param {object} props
 * @param {string|null} props.address - Reverse-geocoded address
 * @param {number|null} props.latitude - GPS latitude
 * @param {number|null} props.longitude - GPS longitude
 * @param {number|null} props.altitude - Altitude in meters
 * @param {number|null} props.accuracy - GPS accuracy in meters
 * @param {number|null} props.bearing - Compass bearing in degrees (camera facing direction)
 * @param {string|null} props.capturedAt - UTC ISO datetime string of capture
 * @param {string|null} props.capturedAtLocal - Pre-formatted local time string
 * @param {boolean} props.showExtended - Show MGRS and datetime (viewer mode)
 */
export const PhotoMetadataOverlay = ({
  address,
  latitude,
  longitude,
  altitude,
  accuracy,
  bearing,
  capturedAt,
  capturedAtLocal,
  showExtended = false,
}) => {
  const hasCoords = latitude != null && longitude != null;

  if (!hasCoords) return null;

  const bearingStr = formatBearing(bearing);
  const coordsStr = formatCoordinates(latitude, longitude);
  const elevStr = formatElevation(altitude);
  const accStr = formatAccuracy(accuracy);
  const mgrsStr = showExtended ? toMGRS(latitude, longitude) : null;
  const dateStr = showExtended ? formatCapturedAt(capturedAtLocal, capturedAt) : null;
  const utcStr = showExtended ? formatCapturedAtUTC(capturedAt) : null;

  return (
    <div className={styles.overlayContainer}>
      {/* Ribbon 1: Address */}
      <div className={styles.ribbon}>
        <span className={styles.ribbonText}>
          {address || <span className={styles.noData}>Address unavailable</span>}
        </span>
      </div>

      {/* Ribbon 2: Bearing | Coordinates | Elevation +/- Accuracy */}
      <div className={styles.ribbon}>
        {bearingStr && (
          <>
            <span>{bearingStr}</span>
            <span className={styles.separator}>|</span>
          </>
        )}
        <span>{coordsStr}</span>
        {elevStr && (
          <>
            <span className={styles.separator}>|</span>
            <span>{elevStr}</span>
          </>
        )}
        {accStr && (
          <>
            <span className={styles.separator}>|</span>
            <span>{accStr}</span>
          </>
        )}
      </div>

      {/* Ribbon 3 (extended): MGRS | Local time | UTC time */}
      {showExtended && (mgrsStr || dateStr) && (
        <div className={styles.ribbon}>
          {mgrsStr && <span>MGRS: {mgrsStr}</span>}
          {mgrsStr && dateStr && <span className={styles.separator}>|</span>}
          {dateStr && <span>{dateStr}</span>}
          {utcStr && (
            <>
              <span className={styles.separator}>|</span>
              <span>{utcStr}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoMetadataOverlay;
